/**
 * B2BNetwork.jsx — Partner Agency Network
 * Rently.ma · Morocco Fleet OS
 *
 * V2 rewrite: CSS variables, ui.jsx imports, i18n t() translations,
 * .grid-3col responsive layout, GlassModal, demo data only.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PageHeader, Badge, StatCard, Card,
  SearchInput, GlassModal,
} from '../components/ui.jsx';

// ── Mock Partner Data (Oriental Region) ─────────────────────────────
const MOCK_PARTNERS = [
  {
    id: '1', agency_name: 'Oujda Auto Location', city: 'Oujda',
    contact_name: 'Youssef El Amrani', phone: '+212 536 68 12 34',
    email: 'contact@oujdaauto.ma', status: 'active',
    joined_at: '2025-01-15', vehicles_shared: 12, network_code: 'OUJ-001',
  },
  {
    id: '2', agency_name: 'Al Hoceima Cars', city: 'Al Hoceima',
    contact_name: 'Nadia Riffi', phone: '+212 539 98 45 67',
    email: 'info@alhoceimacars.ma', status: 'active',
    joined_at: '2025-03-22', vehicles_shared: 8, network_code: 'AHC-002',
  },
  {
    id: '3', agency_name: 'Berkane Location Express', city: 'Berkane',
    contact_name: 'Karim Bouzidi', phone: '+212 536 61 78 90',
    email: 'location@berkane.ma', status: 'pending',
    joined_at: '2025-06-10', vehicles_shared: 0, network_code: 'BRK-003',
  },
  {
    id: '4', agency_name: 'Saïdia Rent', city: 'Saïdia',
    contact_name: 'Amina Tazi', phone: '+212 536 63 22 10',
    email: 'rent@saidiaautos.ma', status: 'active',
    joined_at: '2025-04-02', vehicles_shared: 5, network_code: 'SDA-004',
  },
  {
    id: '5', agency_name: 'Zaio Fleet Services', city: 'Zaio',
    contact_name: 'Rachid Benali', phone: '+212 536 34 56 78',
    email: 'fleet@zaioservices.ma', status: 'suspended',
    joined_at: '2024-11-30', vehicles_shared: 3, network_code: 'ZAO-005',
  },
];

// ── Status config (labelKey pattern) ────────────────────────────────
const STATUS_CFG = {
  active:    { labelKey: 'b2b.statusActive',    variant: 'success' },
  pending:   { labelKey: 'b2b.statusPending',   variant: 'warning' },
  suspended: { labelKey: 'b2b.statusSuspended', variant: 'danger' },
};

// ── Partner Card ────────────────────────────────────────────────────
function PartnerCard({ partner }) {
  const { t } = useTranslation();
  const st = STATUS_CFG[partner.status] || STATUS_CFG.pending;

  return (
    <Card hoverable style={{
      display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)',
      animation: 'scaleIn 300ms var(--ease-smooth) both',
    }}>
      {/* Top: Name + badge */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: 'var(--text-title)', fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
            {partner.agency_name}
          </div>
          <div style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
            {partner.city} — <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: 11 }}>
              {partner.network_code}
            </span>
          </div>
        </div>
        <Badge label={t(st.labelKey)} variant={st.variant} />
      </div>

      {/* Details */}
      <div style={{
        borderTop: '1px solid var(--border)', paddingTop: 'var(--space-md)',
        display: 'flex', flexDirection: 'column', gap: 'var(--space-sm)',
      }}>
        {[
          { label: t('b2b.contactLabel'), value: partner.contact_name },
          { label: t('b2b.phone'), value: partner.phone, mono: true },
          partner.email && { label: t('b2b.email'), value: partner.email, color: 'var(--blue)' },
          { label: t('b2b.memberSince'), value: partner.joined_at },
          {
            label: t('b2b.vehiclesShared'),
            value: partner.vehicles_shared,
            mono: true,
            color: 'var(--gold)',
            bold: true,
          },
        ].filter(Boolean).map((row, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{row.label}</span>
            <span style={{
              fontSize: 13,
              color: row.color || 'var(--text)',
              fontFamily: row.mono ? 'var(--font-mono)' : 'inherit',
              fontWeight: row.bold ? 600 : 400,
            }}>
              {row.value}
            </span>
          </div>
        ))}
      </div>

      {/* Preview Actions */}
      <div style={{ display: 'flex', gap: 'var(--space-sm)', marginTop: 'auto' }}>
        <button disabled style={{
          flex: 1, padding: '10px 0',
          background: 'rgba(91,155,213,0.04)',
          border: '1px solid rgba(91,155,213,0.15)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--text-muted)', cursor: 'not-allowed',
          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
        }}>{t('b2b.preview')}</button>
        <button disabled style={{
          flex: 1, padding: '10px 0',
          background: 'rgba(52,84,179,0.04)',
          border: '1px solid rgba(52,84,179,0.15)',
          borderRadius: 'var(--radius-sm)',
          color: 'var(--text-muted)', cursor: 'not-allowed',
          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
        }}>{t('b2b.preview')}</button>
      </div>
    </Card>
  );
}

// ── Add Partner Modal ───────────────────────────────────────────────
function AddPartnerModal({ onClose, onAdd }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    agency_name: '', city: '', contact_name: '', phone: '', email: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.agency_name || !form.city || !form.contact_name) return;
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 600));
    onAdd({
      id: Date.now().toString(),
      ...form,
      status: 'pending',
      joined_at: new Date().toISOString().slice(0, 10),
      vehicles_shared: 0,
      network_code: form.city.slice(0, 3).toUpperCase() + '-' + String(Math.floor(Math.random() * 900) + 100),
    });
    onClose();
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px',
    background: 'var(--bg)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)', color: 'var(--text)',
    fontSize: 14, outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    fontSize: 'var(--text-label)', color: 'var(--text-secondary)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    marginBottom: 4, display: 'block',
  };

  return (
    <GlassModal open title={t('b2b.addPartner')} subtitle={t('b2b.addSubtitle')} onClose={onClose} width={480}>
      <form onSubmit={handleSubmit}>
        <div style={{ padding: 'var(--space-xl)', display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
          <div>
            <label style={labelStyle}>{t('b2b.agencyName')} *</label>
            <input style={inputStyle} value={form.agency_name} onChange={set('agency_name')}
              placeholder="Ex: Nador Location" required />
          </div>
          <div>
            <label style={labelStyle}>{t('b2b.city')} *</label>
            <input style={inputStyle} value={form.city} onChange={set('city')}
              placeholder="Ex: Nador, Oujda, Berkane" required />
          </div>
          <div>
            <label style={labelStyle}>{t('b2b.contactName')} *</label>
            <input style={inputStyle} value={form.contact_name} onChange={set('contact_name')}
              placeholder={t('blacklist.namePlaceholder')} required />
          </div>
          <div style={{ display: 'flex', gap: 'var(--space-md)' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('b2b.phone')}</label>
              <input style={inputStyle} value={form.phone} onChange={set('phone')}
                placeholder="+212 6XX XX XX XX" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>{t('b2b.email')}</label>
              <input style={inputStyle} value={form.email} onChange={set('email')}
                placeholder="contact@agence.ma" type="email" />
            </div>
          </div>
        </div>
        <div style={{
          padding: 'var(--space-lg) var(--space-xl)',
          borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-md)',
        }}>
          <button type="button" onClick={onClose} style={{
            padding: '10px 20px', background: 'transparent',
            border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
            color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit',
          }}>{t('common.cancel')}</button>
          <button type="submit" disabled={submitting} style={{
            padding: '10px 24px',
            background: submitting ? 'var(--text-muted)' : 'var(--gold)',
            color: 'var(--bg)', border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer',
            fontSize: 14, fontFamily: 'inherit',
          }}>
            {submitting ? t('b2b.adding') : t('common.add')}
          </button>
        </div>
      </form>
    </GlassModal>
  );
}

// ── Main Page ───────────────────────────────────────────────────────
export default function B2BNetwork() {
  const { t } = useTranslation();
  const [partners, setPartners] = useState(MOCK_PARTNERS);
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState('');

  const stats = {
    total: partners.length,
    active: partners.filter(p => p.status === 'active').length,
    pending: partners.filter(p => p.status === 'pending').length,
  };

  const filtered = partners.filter(p => {
    if (!search) return true;
    const q = search.toLowerCase();
    return p.agency_name.toLowerCase().includes(q)
      || p.city.toLowerCase().includes(q)
      || p.contact_name.toLowerCase().includes(q);
  });

  const handleAdd = (newPartner) => {
    setPartners(prev => [newPartner, ...prev]);
  };

  return (
    <div style={{ animation: 'fadeInUp 400ms var(--ease-spring) both' }}>
      {/* Header */}
      <PageHeader
        title={t('b2b.title')}
        subtitle={t('b2b.subtitle')}
        action={
          <button disabled style={{
            padding: '10px 22px', height: 44,
            background: 'rgba(52,84,179,0.15)',
            color: 'var(--text-muted)',
            border: '1px solid rgba(52,84,179,0.3)',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 600, cursor: 'not-allowed', fontSize: 14,
            fontFamily: 'inherit', whiteSpace: 'nowrap',
          }}>
            {t('b2b.invitePartner')} · Q2 2026
          </button>
        }
      />

      {/* Demo mode notice — data is illustrative, no live backend */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 16px',
        background: 'rgba(251,191,36,0.08)',
        border: '1px solid rgba(251,191,36,0.35)',
        borderLeft: '3px solid var(--orange)',
        borderRadius: 'var(--radius-sm)',
        marginBottom: 'var(--space-lg)',
      }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--orange)', flexShrink: 0, display: 'inline-block' }} />
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontStyle: 'italic' }}>
          {t('b2b.demoBanner')}
        </span>
      </div>

      {/* Founding Network Banner */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(52,84,179,0.12) 0%, rgba(52,84,179,0.05) 100%)',
        border: '1px solid rgba(52,84,179,0.3)',
        color: 'var(--gold)', padding: '16px 24px',
        borderRadius: '8px', marginBottom: '24px',
        display: 'flex', alignItems: 'center', gap: '12px',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px', color: 'var(--text)' }}>
            {t('b2b.foundingTitle')}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            {t('b2b.foundingDesc')}
          </div>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid-stats" style={{ marginBottom: 'var(--space-xl)' }}>
        <StatCard label={t('b2b.totalPartners')} value={stats.total} index={0} />
        <StatCard label={t('b2b.active')} value={stats.active} trendUp={true} index={1} />
        <StatCard label={t('b2b.pending')} value={stats.pending} index={2} />
      </div>

      {/* Search */}
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={t('b2b.searchPlaceholder')}
        style={{ maxWidth: 420, marginBottom: 'var(--space-xl)' }}
      />

      {/* Partner Grid */}
      {filtered.length === 0 ? (
        <Card>
          <div style={{ padding: 'var(--space-2xl)', textAlign: 'center' }}>
            <div style={{ fontSize: 'var(--text-title)', fontWeight: 600, color: 'var(--text)', marginBottom: 'var(--space-sm)' }}>
              {t('common.noResults')}
            </div>
            <div style={{ fontSize: 'var(--text-body)', color: 'var(--text-secondary)' }}>
              {t('b2b.noResultsDesc', { query: search })}
            </div>
          </div>
        </Card>
      ) : (
        <div className="grid-3col">
          {filtered.map((p, i) => (
            <div key={p.id} style={{ animationDelay: `${i * 50}ms` }}>
              <PartnerCard partner={p} />
            </div>
          ))}
        </div>
      )}

      {/* Network Value Banner */}
      <div style={{
        marginTop: 'var(--space-2xl)',
        padding: 'var(--space-lg) var(--space-xl)',
        background: 'var(--gold-glow)',
        border: '1px solid var(--border-gold)',
        borderRadius: 'var(--radius-sm)',
        display: 'flex', alignItems: 'center', gap: 'var(--space-md)',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            <strong style={{ color: 'var(--gold)' }}>{t('b2b.foundingTitle')}</strong>
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
            {t('b2b.foundingDesc')}
          </div>
        </div>
      </div>

      {/* Modal */}
      {showAdd && <AddPartnerModal onClose={() => setShowAdd(false)} onAdd={handleAdd} />}
    </div>
  );
}
